import { DadosBasicosController, TipoPessoa } from './../../../models/cadastros/saude/DadosBasicosPrestadorModel';
import { BairroModel } from '@/models/cadastros/saude/BairroModel';
import { servicosSaudeBairrosApi } from '@/api/ServicosSaudeBairro';
import { PrestadorDTO } from "@/api/cadastro/saude/PrestadorDTO";
import { servicosSaudeCidadesApi } from '@/api/ServicosSaudeCidadesApi';
import { servicosSaudeEspecialidadeApi } from '@/api/ServicosSaudeEspecialidadeApi';
import { servicosSaudePrestadorApi } from '@/api/ServicosSaudePrestadorApi';
import { CadastroPrestadorModel } from "@/models/cadastros/saude/CadastroPrestadorModel";
import EspecialidadeModel from "@/models/cadastros/saude/EspecialidadeModel";
import { FiltroPrestadorModel } from "@/models/cadastros/saude/FiltroPrestadorModel";
import { PrestadorPaginationModel } from "@/models/cadastros/saude/PrestadorPaginationModel";
import { PrestadorResumidoModel } from "@/models/cadastros/saude/PrestadorResumidoModel";
import { tiposRamoList } from "@/models/cadastros/saude/RamoModel";
import { tiposAtendimentoList } from "@/models/cadastros/saude/TipoAtendimentoModel";
import { tiposPrestadorList } from "@/models/cadastros/saude/TipoPrestadorModel";
import { CidadeModel } from "@/models/commons/CidadeModel";
import { COMERCIAL } from "@/models/commons/EnderecoModel";
import { estadosList } from "@/models/commons/EstadoModel";
import { tiposTelefoneList } from "@/models/commons/TelefoneModel";
import { CONTA_CORRENTE } from "@/models/commons/TipoContaBancariaModel";

class CadastroPrestadorService {
  pesquisarPrestador(
    filtro: FiltroPrestadorModel
  ): Promise<PrestadorPaginationModel> {
    return servicosSaudePrestadorApi
      .pesquisarPrestador({
        cpfCnpj:
          (filtro.cpfCnpj?.trim() || "") != ""
            ? filtro.cpfCnpj?.trim().replace(/\D/g, "")
            : undefined,
        nome:
          (filtro.nome?.trim() || "") != "" ? filtro.nome?.trim() : undefined,
        tipoPrestador: filtro.tipoPrestador?.valor,
        page: filtro.options.page ? filtro.options.page - 1 : 0,
        size: filtro.options.itemsPerPage,
        sort: filtro.options.sortBy,
      })
      .then((res) => {
        const { content } = res;

        const result: PrestadorPaginationModel = {
          conteudo: (content || []).map((prestadorDTO) => {
            return {
              id: prestadorDTO.id,
              cpfCnpj: prestadorDTO.cpfCnpj,
              nome: prestadorDTO.nome,
              email: prestadorDTO.email,
              tipoPrestador: tiposPrestadorList.find(
                (tp) => prestadorDTO.tipoPrestador == tp.valor
              ),
            } as PrestadorResumidoModel;
          }),
          itemsPerPage: res.size,
          itemsLength: res.totalElements,
          page: filtro.options.page,
        };

        return result;
      });
  }

  async obterEspecialidades(nome: string): Promise<EspecialidadeModel[]> {
    const especialidadesDTO = await servicosSaudeEspecialidadeApi.pesquisarEspecialidade(
      nome
    );
    return especialidadesDTO.map((dto) => {
      return {
        id: dto.id,
        tipoAtendimento: tiposAtendimentoList.find(
          (ta) => ta.name == dto.tipoAtendimento
        ),
        nome: dto.nome,
      };
    });
  }

  async obterCidades(estado: string): Promise<CidadeModel[]> {
    const cidades = await servicosSaudeCidadesApi.pesquisarCidades(estado);
    return cidades.map((c) => {
      return {
        id: c.id,
        nome: c.nome,
        estado: estadosList.find((e) => e.sigla == c.estado),
      };
    });
  }

  async obterBairros(cidade: string): Promise<BairroModel[]> {
    return await servicosSaudeBairrosApi.obterBairrosPorCidade(cidade);
  }

  obterPrestador(id: number): Promise<CadastroPrestadorModel> {
    return servicosSaudePrestadorApi.obterPrestador(id).then((res) => {
      return {
        id: res.id,
        dadosBasicosPrestador: {
          cpfCnpj: res.cpfCnpj,
          nome: res.nome,
          email: res.email,
          flagCadastroCPOM: res.cadastradoCPOM,
          ativo: res.ativo,
          tipoPrestador: tiposPrestadorList.find(
            (tp) => tp.valor == res.tipoPrestador
          ),
          tipoPessoa: res.tipoPessoa ?? DadosBasicosController.isPF(res.cpfCnpj) ? TipoPessoa.FISICA : TipoPessoa.JURIDICA,
          contaBancaria: {
            id: res.contaBancaria.id,
            contaPrincipal: true,
            tipoContaBancaria: CONTA_CORRENTE,
            prestadorTitular: true,
            tipoPessoa: res.contaBancaria.tipoPessoa,
            cpfCnpjTitular: res.contaBancaria.cpfCnpjTitular,
            nomeTitular: res.contaBancaria.nomeTitular,
            codigoBanco: res.contaBancaria.codigoBanco,
            numeroAgencia: res.contaBancaria.numeroAgencia,
            digitoAgencia: res.contaBancaria.digitoAgencia,
            numeroConta: res.contaBancaria.numeroConta,
            digitoConta: res.contaBancaria.digitoConta,
          },
          endereco: {
            id: res.endereco.id,
            cep: res.endereco.cep,
            estado: estadosList.find((e) => e.sigla == res.endereco.estado),
            cidadeModel: {
              id: res.endereco.idCidade,
            },
            bairro: res.endereco.bairro,
            logradouro: res.endereco.logradouro,
            numero: res.endereco.numero,
            complemento: res.endereco.complemento,
            tipo: COMERCIAL,
          },
          telefones: res.telefones.map((t) => {
            return {
              idTelefone: t.id,
              numero: `(${t.ddd}) ${t.numero}`,
              ramal: t.ramal,
              tipo: tiposTelefoneList.find((tt) => tt.nome == t.tipo),
            };
          }),
        },
        especialidadesPrestador: res.especialidades.map((e) => {
          return {
            id: e.id,
            valor: `${parseFloat(e.preco).toFixed(2)}`,
            especialidade: {
              id: e.especialidade?.id,
              nome: e.especialidade?.nome,
              orientacaoPreparo: e.especialidade?.orientacaoPreparo,
              tipoAtendimento: tiposAtendimentoList.find(
                (ta) => ta.name == e.especialidade?.tipoAtendimento
              ),
              ramo: tiposRamoList.find(
                (ramo) => ramo.name == e.especialidade?.ramo
              ),
            },
          };
        }),
        profissionaisPrestador: res.profissionais.map((p) => {
          return {
            id: p.id,
            nome: p.nome,
            numeroRegistro: p.numeroRegistro,
            especialidades: p.especialidades?.map((esp) => {
              return {
                id: esp.id,
                nome: esp.nome,
                orientacaoPreparo: esp.orientacaoPreparo,
                tipoAtendimento: tiposAtendimentoList.find(
                  (ta) => ta.name == esp.tipoAtendimento
                ),
                ramo: tiposRamoList.find((ramo) => ramo.name == esp.ramo),
              };
            }),
          };
        }),
      } as CadastroPrestadorModel;
    });
  }

  salvarPrestador(cadastroPrestador: CadastroPrestadorModel) {
    const cadastroPrestadorDTO: PrestadorDTO = {
      id: cadastroPrestador.id,
      cpfCnpj: cadastroPrestador.dadosBasicosPrestador.cpfCnpj.replace(/\D/g, ""),
      nome: cadastroPrestador.dadosBasicosPrestador.nome,
      email: cadastroPrestador.dadosBasicosPrestador.email,
      cadastradoCPOM: cadastroPrestador.dadosBasicosPrestador.flagCadastroCPOM,
      ativo: cadastroPrestador.dadosBasicosPrestador.ativo,
      tipoPessoa: cadastroPrestador.dadosBasicosPrestador.tipoPessoa,
      tipoPrestador:
        cadastroPrestador.dadosBasicosPrestador.tipoPrestador?.valor || "",
      endereco: {
        id: cadastroPrestador.dadosBasicosPrestador.endereco.id,
        cep: cadastroPrestador.dadosBasicosPrestador.endereco.cep || "",
        idCidade:
          cadastroPrestador.dadosBasicosPrestador.endereco.cidadeModel?.id || 1,
        bairro: cadastroPrestador.dadosBasicosPrestador.endereco.bairro || "",
        logradouro:
          cadastroPrestador.dadosBasicosPrestador.endereco.logradouro || "",
        complemento:
          cadastroPrestador.dadosBasicosPrestador.endereco.complemento,
        numero: cadastroPrestador.dadosBasicosPrestador.endereco.numero
          ? Number(cadastroPrestador.dadosBasicosPrestador.endereco.numero)
          : undefined,
        tipo:
          cadastroPrestador.dadosBasicosPrestador.endereco.tipo?.nome ||
          COMERCIAL.nome,
      },
      telefones: cadastroPrestador.dadosBasicosPrestador.telefones.map(
        (telefoneModel) => {
          return {
            id: telefoneModel.idTelefone,
            tipo: telefoneModel.tipo?.nome || "",
            numero:
              telefoneModel.numero?.split(")")[1].replace(/[^\S*]/g, "") || "",
            ddd: telefoneModel.numero?.split(")")[0].replace(/\D/g, "") || "",
            ramal: telefoneModel.ramal,
          };
        }
      ),
      contaBancaria: {
        id: cadastroPrestador.dadosBasicosPrestador.contaBancaria.id,
        codigoBanco:
          cadastroPrestador.dadosBasicosPrestador.contaBancaria.codigoBanco ||
          "",
        numeroAgencia:
          cadastroPrestador.dadosBasicosPrestador.contaBancaria.numeroAgencia ||
          "",
        digitoAgencia:
          cadastroPrestador.dadosBasicosPrestador.contaBancaria.digitoAgencia ||
          "",
        numeroConta:
          cadastroPrestador.dadosBasicosPrestador.contaBancaria.numeroConta ||
          "",
        digitoConta:
          cadastroPrestador.dadosBasicosPrestador.contaBancaria.digitoConta ||
          "",
        contaPrincipal: true,
        cpfCnpjTitular: cadastroPrestador.dadosBasicosPrestador.cpfCnpj.replace(
          /\D/g,
          ""
        ),
        nomeTitular: cadastroPrestador.dadosBasicosPrestador.nome.length <= 30 ? cadastroPrestador.dadosBasicosPrestador.nome : cadastroPrestador.dadosBasicosPrestador.nome.substring(0, 30),
        tipoPessoa: "JURIDICA",
      },
      especialidades: cadastroPrestador.especialidadesPrestador.map((ep) => {
        return {
          id: ep.id,
          especialidade: {
            id: ep.especialidade?.id || 0,
            nome: ep.especialidade?.nome || "",
            tipoAtendimento: ep.especialidade?.tipoAtendimento?.name || "",
          },
          preco: ep.valor ? ep.valor : "0",
        };
      }),
      profissionais: cadastroPrestador.profissionaisPrestador.map((pp) => {
        return {
          id: pp.id,
          nome: pp.nome || "",
          numeroRegistro: pp.numeroRegistro || "",
          especialidades: pp.especialidades.map((profissionalEspecialidade) => {
            return {
              id: profissionalEspecialidade?.id || 0,
              nome: profissionalEspecialidade?.nome || "",
              tipoAtendimento:
                profissionalEspecialidade.tipoAtendimento?.name || "",
            };
          }),
        };
      }),
    };
    if (cadastroPrestadorDTO.id) {
      return servicosSaudePrestadorApi.atualizarPrestador(cadastroPrestadorDTO);
    } else {
      return servicosSaudePrestadorApi.cadastrarPrestador(cadastroPrestadorDTO);
    }
  }

  excluirPrestador(id: number): Promise<any> {
    return servicosSaudePrestadorApi.excluirPrestador(id);
  }

  async pesquisarPrestadorPorNome(nome: string): Promise<PrestadorResumidoModel[]> {
    const prestadores = await servicosSaudePrestadorApi.pesquisarPrestadorPorNome(nome);
    return prestadores.map((prestadorDTO) => {
      return {
        id: prestadorDTO.id,
        cpfCnpj: prestadorDTO.cpfCnpj,
        nome: prestadorDTO.nome,
        email: prestadorDTO.email,
        tipoPrestador: tiposPrestadorList.find(
          (tp) => prestadorDTO.tipoPrestador == tp.valor
        ),
      } as PrestadorResumidoModel;
    });
  }

  async pesquisarPrestadorPorEspecialidadeENome(isEspecialidade: number, nome: string): Promise<PrestadorResumidoModel[]> {
    const prestadores = await servicosSaudePrestadorApi.pesquisarPrestadorPorEspecialidadeENome(isEspecialidade, nome);
    return prestadores.map((prestadorDTO) => {
      return {
        id: prestadorDTO.id,
        cpfCnpj: prestadorDTO.cpfCnpj,
        nome: prestadorDTO.nome,
        email: prestadorDTO.email,
        tipoPrestador: tiposPrestadorList.find(
          (tp) => prestadorDTO.tipoPrestador == tp.valor
        ),
      } as PrestadorResumidoModel;
    });
  }

  verificarCNPJCadastrado(cnpj: string): Promise<boolean> {
    return servicosSaudePrestadorApi.pesquisarPrestadorCNPJCadastrado(cnpj)
    .then((prestador: PrestadorResumidoModel | null) => {
      return !!prestador && !!prestador.id;
    });
  }
}



export const cadastroPrestadorService = new CadastroPrestadorService();
